// UF-01: one amber line when the kg per bin looks wrong. Never blocks Tip.
// The range comes from this business's own delivery tips of the last 90 days,
// per bin type (p01..p99, no line until a bin type has 20 tips). No line over a
// pending or failed read, and none offline with nothing cached.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { readState } from '@/lib/readState'
import { binGroupKey, binRanges, kgPerBinWarning, rowsToSamples, type TipRow } from '@/lib/tippingRange'
import { useTipNetKg } from './TipNetPreview'

const DAYS = 90

interface Row extends TipRow {
  tipping_id: string
}

function useBinRanges() {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: ['tip-bin-ranges', tenantId],
    enabled: !!tenantId,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const since = new Date(Date.now() - DAYS * 86_400_000).toISOString()
      const { rows, error } = await fetchAllPaged<Row>((from, to) =>
        withReadTimeout(
          supabase.from('tipping_log')
            .select('tipping_id, bin_quantity, net_weight_kg, deliveries!inner(fruit_bin_type_id, fruit_container_type, bin_types(label))')
            .eq('tenant_id', tenantId!)
            .not('delivery_id', 'is', null)
            .gte('tipped_at', since)
            .order('tipping_id')
            .range(from, to) as unknown as PromiseLike<{ data: Row[] | null; error: { message: string } | null }>,
          'Tipping: recent tips for the bin range',
        ),
      )
      if (error) throw new Error(error)
      return binRanges(rowsToSamples(rows))
    },
  })
}

export default function TipRangeHint({ deliveryId, binTypeId, containerType, gross, binQty }: {
  deliveryId: string
  binTypeId?: string | null
  containerType?: string | null
  gross: number
  binQty: number
}) {
  const rangesQ = useBinRanges()
  const netKg = useTipNetKg(deliveryId, gross, binQty)
  if (readState(rangesQ) !== 'usable' || !rangesQ.data) return null
  const key = binGroupKey(binTypeId, containerType)
  const msg = kgPerBinWarning(key ? rangesQ.data.get(key) : undefined, netKg, binQty)
  if (!msg) return null
  return <p className="text-caption text-warning mt-1">{msg}</p>
}
